import { all, one, type Target } from './dom';
import type { NameValue } from './http';

/**
 * jQuery's serializeArray(): successful controls only -- named, enabled, not
 * a button or file input, checkboxes/radios only when checked, every selected
 * option of a multi-select.
 */
export function serialize(target: Target): NameValue[] {
    const form = one(target);
    if (!form) return [];
    const pairs: NameValue[] = [];
    const controls = form instanceof HTMLFormElement
        ? Array.from(form.elements)
        : Array.from(form.querySelectorAll('input, select, textarea'));
    for (const control of controls) {
        if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement)) continue;
        if (!control.name || control.disabled) continue;
        if (control instanceof HTMLInputElement) {
            const type = control.type;
            if (type === 'submit' || type === 'button' || type === 'reset' || type === 'file' || type === 'image') continue;
            if ((type === 'checkbox' || type === 'radio') && !control.checked) continue;
            pairs.push({ name: control.name, value: control.value });
        } else if (control instanceof HTMLSelectElement) {
            for (const option of Array.from(control.selectedOptions)) {
                pairs.push({ name: control.name, value: option.value });
            }
        } else {
            pairs.push({ name: control.name, value: control.value });
        }
    }
    return pairs;
}

/** serialize() as a query string. */
export function serializeQuery(target: Target): string {
    const params = new URLSearchParams();
    for (const pair of serialize(target)) params.append(pair.name, pair.value);
    return params.toString();
}

// ---------------------------------------------------------------- UTC time-of-day bridge

/*
 * Policy windows (ResourcePolicy AccessTimeStart/End, PersonPolicy
 * LoginTimeStart/End) are stored as UTC times-of-day so the server can gate on
 * DateTime.UtcNow.TimeOfDay without timezone ambiguity; operators think in
 * local time. Inputs marked data-mcp-utc-time are converted to local on
 * display and back to UTC on submit. HH:mm only; DST follows today's offset.
 */
function parseHHMM(s: string | null | undefined): { h: number; m: number } | null {
    if (!s) return null;
    const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(s);
    if (!m) return null;
    const h = Number(m[1]);
    const mm = Number(m[2]);
    if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
    return { h, m: mm };
}

function fmtHHMM(h: number, m: number): string {
    return `${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}`;
}

export function utcToLocal(hhmm: string): string {
    const p = parseHHMM(hhmm);
    if (!p) return hhmm;
    const d = new Date();
    d.setUTCHours(p.h, p.m, 0, 0);
    return fmtHHMM(d.getHours(), d.getMinutes());
}

export function localToUtc(hhmm: string): string {
    const p = parseHHMM(hhmm);
    if (!p) return hhmm;
    const d = new Date();
    d.setHours(p.h, p.m, 0, 0);
    return fmtHHMM(d.getUTCHours(), d.getUTCMinutes());
}

const utcInputs = 'input[type="time"][data-mcp-utc-time]';

/** Convert every marked input under root once; re-running is a no-op. */
export function applyUtcToInputs(root?: Target | Document): void {
    const scope = root instanceof Document ? root : root ? one(root) : document;
    if (!scope) return;
    for (const input of Array.from(scope.querySelectorAll<HTMLInputElement>(utcInputs))) {
        if (input.dataset['mcpUtcApplied'] === 'true') continue;
        input.dataset['mcpUtcOriginal'] = input.value;
        input.value = utcToLocal(input.value);
        input.dataset['mcpUtcApplied'] = 'true';
    }
}

/** serialize() with the marked time inputs converted back to UTC. */
export function serializeUtc(target: Target): NameValue[] {
    const form = one(target);
    const pairs = serialize(target);
    if (!form) return pairs;
    const utcNames = new Set(Array.from(form.querySelectorAll<HTMLInputElement>(utcInputs)).map(i => i.name).filter(Boolean));
    return pairs.map(pair => utcNames.has(pair.name) && pair.value
        ? { name: pair.name, value: localToUtc(pair.value) }
        : pair);
}

/**
 * A checkbox marked data-action="Shared.ToggleTimeWindow" with
 * data-target governs the time inputs inside the target: checked ("no time
 * restriction") disables them so they serialize as absent -> null on the
 * server -> "any time of day".
 */
export function toggleTimeWindow(this: HTMLElement): void {
    const checkbox = this as HTMLInputElement;
    const target = checkbox.dataset['target'] ? one(checkbox.dataset['target']) : null;
    if (!target) return;
    const noWindow = checkbox.checked;
    for (const input of Array.from(target.querySelectorAll<HTMLInputElement>('input[type="time"]'))) input.disabled = noWindow;
    target.classList.toggle('opacity-50', noWindow);
}

/** Start a time-window toggle from the inputs' state: both empty = "no restriction". */
export function initTimeWindow(checkboxId: string, targetSelector: string): void {
    const target = one(targetSelector);
    if (!target) return;
    const inputs = Array.from(target.querySelectorAll<HTMLInputElement>('input[type="time"]'));
    const hasValue = inputs.some(i => i.value);
    const checkbox = document.getElementById(checkboxId) as HTMLInputElement | null;
    if (checkbox) checkbox.checked = !hasValue;
    for (const input of inputs) input.disabled = !hasValue;
    target.classList.toggle('opacity-50', !hasValue);
}

let bound = false;

/** Document-level submit hook for data-mcp-utc-form forms + the first-load pass. */
export function initTimeOfDay(): void {
    if (bound) return;
    bound = true;
    document.addEventListener('submit', event => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-mcp-utc-form')) return;
        const stash: Array<{ input: HTMLInputElement; local: string }> = [];
        for (const input of Array.from(form.querySelectorAll<HTMLInputElement>(utcInputs))) {
            stash.push({ input, local: input.value });
            input.value = localToUtc(input.value);
        }
        // Restore after the browser has read the values, so a validation bounce shows local time.
        setTimeout(() => { for (const s of stash) s.input.value = s.local; }, 0);
    });
    const run = (): void => applyUtcToInputs(document);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
    else run();
}

/** Reset every text-like input, checkbox and select under a root (filter "Clear" buttons). */
export function resetFields(root: Target): void {
    for (const scope of all(root)) {
        for (const input of Array.from(scope.querySelectorAll<HTMLInputElement>('input[type="text"], input[type="email"], input[type="tel"], input[type="search"], input[type="date"]'))) input.value = '';
        for (const input of Array.from(scope.querySelectorAll<HTMLInputElement>('input[type="checkbox"]'))) input.checked = false;
        for (const select of Array.from(scope.querySelectorAll<HTMLSelectElement>('select'))) select.selectedIndex = 0;
    }
}

const AUTOSUBMIT = 'data-mcp-autosubmit';
const AUTOSUBMIT_DONE = 'data-mcp-autosubmit-armed';

/**
 * Submit a server-rendered form by itself, after the delay its attribute names
 * (milliseconds; 0 or an unparsable value means immediately).
 *
 * This exists so a partial can hand the browser a top-level navigation without
 * carrying an inline <script> -- which a swapped-in partial cannot rely on and
 * the strict Content-Security-Policy forbids outright. The one caller today is
 * Home/_ForcedSignOut, whose POST has to be a real navigation because the OIDC
 * end_session round trip cannot happen over XHR.
 *
 * Marked once armed: enhancers run over every swap, and the same form landing
 * in a second enhance pass must not queue a second submit.
 */
export function enhanceAutoSubmitForms(root: ParentNode = document): void {
    for (const form of Array.from(root.querySelectorAll<HTMLFormElement>(`form[${AUTOSUBMIT}]`))) {
        if (form.hasAttribute(AUTOSUBMIT_DONE)) continue;
        form.setAttribute(AUTOSUBMIT_DONE, 'true');
        const delay = Number.parseInt(form.getAttribute(AUTOSUBMIT) ?? '', 10);
        const submit = (): void => form.submit();
        if (Number.isFinite(delay) && delay > 0) setTimeout(submit, delay);
        else submit();
    }
}

const DISTINCT_GROUP = 'data-mcp-distinct-group';

/**
 * Keep a group of <select>s mutually exclusive: an option already chosen in one
 * is disabled in the others.
 *
 * Both challenge-question pickers (invite acceptance and the forced setup page)
 * carried a byte-identical copy of this as an inline <script>, under two
 * different class names. The rule they enforce is the server's -- the answers
 * must name distinct questions -- and showing it at picker time is what keeps the
 * API error from ever firing.
 *
 * Members opt in with data-mcp-distinct-group="<name>"; every select sharing a
 * name is one group, so a page may host more than one.
 */
export function enhanceDistinctSelectGroups(root: ParentNode = document): void {
    const groups = new Map<string, HTMLSelectElement[]>();
    for (const select of Array.from(root.querySelectorAll<HTMLSelectElement>(`select[${DISTINCT_GROUP}]`))) {
        const name = select.getAttribute(DISTINCT_GROUP) ?? '';
        const members = groups.get(name);
        if (members) members.push(select);
        else groups.set(name, [select]);
    }

    for (const members of groups.values()) {
        // One select can never collide with itself.
        if (members.length < 2) continue;

        const sync = (): void => {
            const picked = new Set<string>();
            for (const select of members) {
                if (select.value) picked.add(select.value);
            }
            for (const select of members) {
                for (const option of Array.from(select.options)) {
                    // The empty "-- choose --" placeholder stays selectable everywhere.
                    if (!option.value) continue;
                    // "Taken by someone else", not "taken by whoever claimed it last":
                    // a select must never disable the value it is currently holding.
                    // Keying an owner map by value made the last writer the owner, so
                    // when two selects arrived holding the SAME value -- which the
                    // server can render -- the earlier one greyed out its own answer.
                    option.disabled = option.value !== select.value && picked.has(option.value);
                }
            }
        };

        for (const select of members) {
            // Enhancers run over every swap, so a select that has already been wired
            // must not collect a second listener.
            if (select.dataset.mcpDistinctBound === 'true') continue;
            select.dataset.mcpDistinctBound = 'true';
            select.addEventListener('change', sync);
        }
        sync();
    }
}

const SET_SELECT = 'data-mcp-set-select';
const SET_VALUE = 'data-mcp-set-value';

/**
 * Point a control that lives OUTSIDE a partial at a value the partial knows.
 *
 * The dashboard is the caller: the rail's domain picker renders before the view
 * does, so the view is the only thing that knows which domain it is showing.
 * It used to say so in an inline <script>; it declares it now, because a partial
 * cannot rely on its own scripts running and the strict Content-Security-Policy
 * forbids them outright.
 *
 * The marker is searched from the document, not from the swapped root: the target
 * is by definition somewhere else on the page.
 */
export function enhanceSelectSync(root: ParentNode = document): void {
    for (const marker of Array.from(root.querySelectorAll<HTMLElement>(`[${SET_SELECT}]`))) {
        const id = marker.getAttribute(SET_SELECT);
        const value = marker.getAttribute(SET_VALUE);
        if (!id || value === null) continue;
        const target = document.getElementById(id);
        if (target instanceof HTMLSelectElement || target instanceof HTMLInputElement) {
            target.value = value;
        }
    }
}
